if (!customElements.get('fc-why-wet-food')) {
  class FcWhyWetFood extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.features = [];
      this.featureHandlers = [];
      this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleStageClick = this.handleStageClick.bind(this);
      this.handleSectionLinkClick = this.handleSectionLinkClick.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.features = Array.from(this.querySelectorAll('[data-fc-feature]'));
      this.sectionLink = this.querySelector('[data-fc-section-link]');
      this.features.forEach((feature) => this.bindFeature(feature));
      this.bindMediaQuery();

      if (this.sectionLink) {
        this.addEventListener('click', this.handleStageClick);
      }

      if (window.Shopify && Shopify.designMode) {
        if (this.sectionLink) {
          this.dataset.linkDisabled = 'true';
          this.sectionLink.setAttribute('tabindex', '-1');
        }
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.sectionLink?.addEventListener('click', this.handleSectionLinkClick);
      }

      window.addEventListener('resize', this.handleResize);
      this.syncMode();
    }

    disconnectedCallback() {
      this.featureHandlers.forEach(({ feature, trigger, handlers }) => {
        trigger.removeEventListener('click', handlers.click);
        feature.removeEventListener('pointerenter', handlers.pointerenter);
        feature.removeEventListener('pointerleave', handlers.pointerleave);
        feature.removeEventListener('focusin', handlers.focusin);
        feature.removeEventListener('focusout', handlers.focusout);
      });
      this.featureHandlers = [];

      if (this.sectionLink) {
        this.removeEventListener('click', this.handleStageClick);
      }

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.removeListener(this.handleMediaChange);
      }

      if (window.Shopify && Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.sectionLink?.removeEventListener('click', this.handleSectionLinkClick);
        this.removeAttribute('data-link-disabled');
      }

      window.removeEventListener('resize', this.handleResize);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      this.initialized = false;
    }

    bindFeature(feature) {
      const trigger = feature.querySelector('[data-fc-feature-trigger]');
      if (!trigger) return;

      const handlers = {
        click: () => this.handleClick(feature),
        pointerenter: () => this.handlePointerEnter(feature),
        pointerleave: () => this.handlePointerLeave(feature),
        focusin: () => this.handleFocusIn(feature),
        focusout: (event) => this.handleFocusOut(feature, event),
      };

      trigger.addEventListener('click', handlers.click);
      feature.addEventListener('pointerenter', handlers.pointerenter);
      feature.addEventListener('pointerleave', handlers.pointerleave);
      feature.addEventListener('focusin', handlers.focusin);
      feature.addEventListener('focusout', handlers.focusout);

      this.featureHandlers.push({ feature, trigger, handlers });
    }

    bindMediaQuery() {
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.addListener(this.handleMediaChange);
      }
    }

    handleMediaChange() {
      this.syncMode();
    }

    handleResize() {
      if (this.resizeFrame) return;
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateDividerWidth();
      });
    }

    handleClick(feature) {
      if (this.isMobile()) {
        this.setExpanded(feature, true, false);
        return;
      }

      const isLocked = feature.dataset.locked === 'true';
      this.setExpanded(feature, !isLocked, !isLocked);
    }

    handlePointerEnter(feature) {
      if (this.isMobile() || !this.hoverQuery.matches) return;
      this.setExpanded(feature, true);
    }

    handlePointerLeave(feature) {
      if (this.isMobile() || feature.dataset.locked === 'true') return;
      if (feature.contains(document.activeElement)) return;
      this.setExpanded(feature, false);
    }

    handleFocusIn(feature) {
      if (this.isMobile()) return;
      this.setExpanded(feature, true);
    }

    handleFocusOut(feature, event) {
      if (this.isMobile() || feature.dataset.locked === 'true') return;
      if (event.relatedTarget && feature.contains(event.relatedTarget)) return;
      if (feature.matches(':hover') && this.hoverQuery.matches) return;
      this.setExpanded(feature, false);
    }

    handleBlockSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;
      const feature = event.target.closest('[data-fc-feature]');
      if (!feature || !this.contains(feature)) return;

      this.setExpanded(feature, true, true);
    }

    handleStageClick(event) {
      if (!this.sectionLink || event.target === this.sectionLink || this.sectionLink.contains(event.target)) return;

      if (window.Shopify && Shopify.designMode) {
        event.preventDefault();
        return;
      }

      const href = this.sectionLink.getAttribute('href');
      if (!href || event.defaultPrevented) return;

      if (event.metaKey || event.ctrlKey) {
        window.open(href, '_blank', 'noopener');
        return;
      }

      window.location.href = href;
    }

    handleSectionLinkClick(event) {
      event.preventDefault();
    }

    syncMode() {
      const mobile = this.isMobile();
      this.setAttribute('data-mobile-features-position', this.dataset.mobileFeaturesPosition || this.getMobilePositionMode());

      this.features.forEach((feature) => {
        if (mobile) {
          this.setExpanded(feature, true, false);
        } else {
          this.setExpanded(feature, feature.dataset.locked === 'true');
        }
      });

      this.updateDividerWidth();
    }

    updateDividerWidth() {
      if (this.isMobile()) {
        this.style.removeProperty('--fc-feature-divider-width');
        return;
      }

      const titles = Array.from(this.querySelectorAll('.fc-why-wet-food__feature-title'));
      const longestTitleWidth = titles.reduce((width, title) => {
        return Math.max(width, title.getBoundingClientRect().width);
      }, 0);

      if (!longestTitleWidth) return;

      const extra = parseFloat(window.getComputedStyle(this).getPropertyValue('--fc-feature-divider-extra')) || 28;
      this.style.setProperty('--fc-feature-divider-width', `${Math.ceil(longestTitleWidth + extra)}px`);
    }

    getMobilePositionMode() {
      const section = this.closest('.fc-why-wet-food');
      if (!section) return 'bottom';
      return section.dataset.mobileFeaturesPosition || 'bottom';
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    setExpanded(feature, expanded, locked) {
      const trigger = feature.querySelector('[data-fc-feature-trigger]');
      const description = feature.querySelector('[data-fc-feature-description]');
      const expandedString = expanded ? 'true' : 'false';

      feature.dataset.expanded = expandedString;
      trigger?.setAttribute('aria-expanded', expandedString);
      description?.setAttribute('aria-hidden', expanded ? 'false' : 'true');

      if (typeof locked === 'boolean') {
        feature.dataset.locked = locked ? 'true' : 'false';
      }
    }
  }

  customElements.define('fc-why-wet-food', FcWhyWetFood);
}
